export interface GridTarget {
  gridLinkId: string;
  rowLinkId?: string;
  columnId?: string;
}

export interface GridOption {
  id: string;
  label: string;
  value: string;
}

export interface GridSchema {
  linkId: string;
  conditional: boolean;
  properties: Record<string, any>;
  columns: Record<string, any>[];
  rows: Record<string, any>[];
  cellSettings: Record<string, Record<string, { mode?: string }>>;
  cells: Record<string, Record<string, Record<string, any>>>;
}

export function getGridProperties(control: any): Record<string, any> {
  let extensionProperties = {};
  const extension = control?.extension?.find((entry: any) => entry.url === 'urn:ccs:ext:ui');
  if (extension?.valueString) {
    try {
      extensionProperties = JSON.parse(extension.valueString);
    } catch {}
  }
  return { ...extensionProperties, ...control, ...control?.componentProps, ...control?.componentMetadata?.properties };
}

export function getGridSchema(control: any): GridSchema | null {
  if (!control) {
    return null;
  }
  const properties = getGridProperties(control);
  const rawType = properties.controlType || control.componentType || properties.type;
  const type =
    rawType === 'HxTableGrid'
      ? 'table-grid'
      : rawType === 'HxConditionalTableGrid'
        ? 'conditional-table-grid'
        : rawType;
  if (type !== 'table-grid' && type !== 'conditional-table-grid') {
    return null;
  }
  const conditional = type === 'conditional-table-grid';
  const staticData = properties.staticData ?? {};
  const configuration = conditional ? staticData.conditionalColumnConfig : { ...properties, ...staticData };
  if (!configuration) {
    return null;
  }
  const columns = Array.isArray(configuration.columns) ? configuration.columns : [];
  const rows = getConfiguredRows(configuration);
  if (!hasUniqueIds(columns, 'id') || !hasUniqueIds(rows, 'linkId')) {
    return null;
  }
  return {
    linkId: control.linkId || control.id,
    conditional,
    properties,
    columns,
    rows,
    cells: getConfiguredCells(control),
    cellSettings: configuration.cellSettings ?? {},
  };
}

function getConfiguredRows(configuration: Record<string, any>): Record<string, any>[] {
  if (Array.isArray(configuration.sections)) {
    return configuration.sections.flatMap((section: any) => section.rows ?? []);
  }
  return Array.isArray(configuration.rows) ? configuration.rows : [];
}

function hasUniqueIds(entries: Record<string, any>[], key: string): boolean {
  return (
    entries.length > 0 &&
    entries.every((entry) => typeof entry[key] === 'string' && entry[key].length > 0) &&
    new Set(entries.map((entry) => entry[key])).size === entries.length
  );
}

function getConfiguredCells(control: any): GridSchema['cells'] {
  const cells: GridSchema['cells'] = Object.create(null);
  const visit = (item: any): void => {
    const cell = getGridProperties(item);
    const rowId = cell.__conditionalTableGridRowLinkId;
    const columnId = cell.__conditionalTableGridColId;
    if (rowId && columnId) {
      (cells[rowId] ??= Object.create(null))[columnId] = cell;
    }
    for (const child of item.items ?? item.item ?? item.children ?? []) {
      visit(child);
    }
  };
  visit(control);
  return cells;
}

export function getGridCellProperties(schema: GridSchema, rowId: string, columnId: string): Record<string, any> {
  const column = schema.columns.find((entry) => entry.id === columnId) ?? {};
  const row = schema.rows.find((entry) => entry.linkId === rowId);
  const cell = schema.cells[rowId]?.[columnId] ?? {};
  const rowOptions = row?.rowOptions?.[columnId];
  return {
    ...column,
    ...cell,
    inputType: column.inputType || schema.properties.inputType || 'radio',
    options: cell.options?.length ? cell.options : rowOptions?.length ? rowOptions : (column.options ?? []),
  };
}

export function isGridTargetValid(schema: GridSchema, target: GridTarget): boolean {
  return (
    schema.linkId === target.gridLinkId &&
    (!target.rowLinkId || schema.rows.some((row) => row.linkId === target.rowLinkId)) &&
    (!target.columnId || schema.columns.some((column) => column.id === target.columnId))
  );
}

export function getGridCellOptions(schema: GridSchema, rowId: string, columnId: string): GridOption[] {
  const properties = getGridCellProperties(schema, rowId, columnId);
  if (properties.inputType === 'checkbox') {
    return [
      { id: 'true', value: 'true', label: 'Checked' },
      { id: 'false', value: 'false', label: 'Unchecked' },
    ];
  }
  if (properties.inputType === 'rating') {
    const configuredScale = Number(properties.ratingScale ?? 5);
    const scale = Number.isFinite(configuredScale) ? Math.max(0, Math.min(100, Math.trunc(configuredScale))) : 5;
    return Array.from({ length: scale + 1 }, (_, index) => ({
      id: String(index),
      value: String(index),
      label: String(index),
    }));
  }
  if (!schema.conditional && properties.inputType === 'radio') {
    return [{ id: columnId, value: String(properties.value || columnId), label: String(properties.label || columnId) }];
  }
  const options = properties.options
    .filter((option: any) => option.value !== undefined && option.value !== null)
    .map((option: any) => ({
      id: String(option.value),
      value: String(option.value),
      label: String(option.label ?? option.value),
    }));
  return properties.inputType === 'radio' && !options.length
    ? [{ id: '__selected__', value: '__selected__', label: String(properties.label || columnId) }]
    : options;
}

export function getGridOtherValue(properties: Record<string, any>): string | null {
  if (properties.showOtherInput === false) {
    return null;
  }
  const other = properties.options?.find((option: any) => {
    const label = String(option.label ?? '')
      .trim()
      .toLowerCase();
    const value = String(option.value ?? '')
      .trim()
      .toLowerCase();
    return (
      (properties.otherValue !== undefined && option.value === properties.otherValue) ||
      ['other', 'others'].includes(label) ||
      ['other', 'others'].includes(value) ||
      /^(other[ :\-])/.test(label) ||
      /^(other[_\-])/.test(value)
    );
  });
  return properties.otherValue ?? other?.value ?? (properties.showOtherInput === true ? 'other' : null);
}
